import { useCallback } from "react";
import { useSettingsReportValue } from "./useReportStorage";

// Lives in the `settings_report` localStorage object, so it survives
// reloads and is shared by every component that reads this hook.
export const FEDERAL_TAX_RATE_KEY = "federal_tax_rate_strategy";
export const DEFAULT_FEDERAL_TAX_RATE = 17;
export const MIN_TAX_RATE = 0;
export const MAX_TAX_RATE = 100;

export const clampTaxRate = (value: number) =>
  Math.min(Math.max(value, MIN_TAX_RATE), MAX_TAX_RATE);

/**
 * Federal tax rate (percent) used by the strategy report.
 *
 * A browser-local setting rather than a stored per-client one — see
 * StrategyTaxStateSetting for the state rate, which is persisted server
 * side as a TaxRate mapping.
 */
export const useFederalTaxRate = (): [number, (value: number) => void] => {
  const [storedValue, setStoredValue] = useSettingsReportValue(
    FEDERAL_TAX_RATE_KEY,
    DEFAULT_FEDERAL_TAX_RATE,
  );

  const parsed = Number(storedValue);
  const federalTaxRate = Number.isFinite(parsed)
    ? clampTaxRate(parsed)
    : DEFAULT_FEDERAL_TAX_RATE;

  const setFederalTaxRate = useCallback(
    (value: number) => setStoredValue(clampTaxRate(value)),
    [setStoredValue],
  );

  return [federalTaxRate, setFederalTaxRate];
};
